import type { CatalogItem, PayoutEventDay, PayoutNurse, PayoutPayment, PayoutRates, PayoutSchedule, PayoutShare, RevenueShare, RevenueShareInput, StaffListItem, UnpaidPayouts, UnpaidPeriod } from "@vaulted/shared";
import { PAYOUT_SCHEDULES, payoutPayeeId, PAYOUT_SCHEDULE_LABELS, formatCents, parseDollars } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router";
import { del, errorMessage, get, post, put } from "../api";
import { ExportButtons } from "../components/ExportButtons";
import { type ReportTable, dollars } from "../components/exportReport";
import { Alert, Badge, Button, Checkbox, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, Tabs } from "../components/ui";
import { useSession } from "../session";
import { dayLabel } from "./Calendar";
import { useCatalog } from "./Catalog";

type Settings = PayoutRates & { schedule: PayoutSchedule; anchorDate: string; shares: RevenueShare[] };

/** Payouts: what's still owed to nurses and revenue shares, by pay period. Paid ones are in Reports → Payouts. */
export function PayoutsPage() {
  const { tab } = useParams();
  const { can } = useSession();
  const manage = can("payouts.manage");
  if (tab && tab !== "rates") return <Navigate to="/payouts" replace />;
  if (tab === "rates" && !manage) return <Navigate to="/payouts" replace />;
  return (
    <div>
      <PageHeader title="Payouts" description="Commission on what clients paid for services, add-ons, and travel, plus tips; and revenue shares. Visits count on the day the bill was paid." />
      {manage && (
        <Tabs
          items={[
            { to: "/payouts", label: "Unpaid", end: true },
            { to: "/payouts/rates", label: "Pay rates" },
          ]}
        />
      )}
      {tab === "rates" ? <RatesTab /> : <UnpaidTab />}
    </div>
  );
}

export const periodRange = (start: string, end: string) => `${dayLabel(start, { month: "short", day: "numeric" })} – ${dayLabel(end, { month: "short", day: "numeric", year: "numeric" })}`;

/** One payee in a period: a nurse or a revenue share. */
type Payee = { kind: "staff"; nurse: PayoutNurse } | { kind: "share"; share: PayoutShare };
const payeesOf = (p: Pick<UnpaidPeriod, "nurses" | "shares">): Payee[] => [...p.nurses.map((nurse): Payee => ({ kind: "staff", nurse })), ...p.shares.map((share): Payee => ({ kind: "share", share }))];

/** Spreadsheet tables for some payees (a period, or a list of payments). */
export function payoutTables(rows: { period: string; payee: Payee }[]): ReportTable[] {
  const nurses = rows.flatMap((r) => (r.payee.kind === "staff" ? [{ period: r.period, n: r.payee.nurse }] : []));
  const shares = rows.flatMap((r) => (r.payee.kind === "share" ? [{ period: r.period, sh: r.payee.share }] : []));
  return [
    {
      title: "Summary",
      columns: ["Period", "Payee", "Visits", "Commission", "Tips", "Bonuses and corrections", "Amount"],
      rows: [
        ...nurses.map(({ period, n }) => [period, n.name, n.visits, dollars(n.commissionCents), dollars(n.tipsCents), dollars(n.adjustmentsCents), dollars(n.totalCents)]),
        ...shares.map(({ period, sh }) => [period, `${sh.name} (${sh.percent}%)`, "", "", "", sh.adjustmentsCents ? dollars(sh.adjustmentsCents) : "", dollars(sh.amountCents)]),
      ],
    },
    {
      title: "Nurse lines",
      columns: ["Period", "Nurse", "Date", "Client", "Item", "Qty", "Price", "Base", "Overhead", "Percent", "Commission", "Note"],
      rows: nurses.flatMap(({ period, n }) => n.lines.map((l) => [period, n.name, l.date, l.client, l.name, l.quantity, dollars(l.priceCents), dollars(l.baseCents), dollars(l.overheadCents ?? 0), l.percent, dollars(l.amountCents), [l.event && `${l.event}${l.sharedBy ? ` (shared by ${l.sharedBy})` : ""}`, l.note].filter(Boolean).join("; ")])),
    },
    {
      title: "Revenue share lines",
      columns: ["Period", "Share", "Date", "Client", "Item", "Where", "Base", "Amount"],
      rows: shares.flatMap(({ period, sh }) => sh.lines.map((l) => [period, `${sh.name} (${sh.percent}%)`, l.date, l.client, l.name, l.context, dollars(l.baseCents), dollars(l.amountCents)])),
    },
    {
      title: "Bonuses and corrections",
      columns: ["Period", "Payee", "Amount", "Note", "Added by"],
      rows: [
        ...nurses.flatMap(({ period, n }) => n.adjustments.map((a) => [period, n.name, dollars(a.amountCents), a.note, a.by])),
        ...shares.flatMap(({ period, sh }) => (sh.adjustments ?? []).map((a) => [period, `${sh.name} (revenue share)`, dollars(a.amountCents), a.note, a.by])),
      ],
    },
  ];
}

function UnpaidTab() {
  const { can } = useSession();
  const manage = can("payouts.manage");
  const data = useQuery({ queryKey: ["payouts"], queryFn: () => get<UnpaidPayouts>("/api/payouts") });
  const d = data.data;
  if (data.isLoading) return <Loading />;
  if (data.error || !d) return <Alert>{errorMessage(data.error)}</Alert>;
  return (
    <div className="flex flex-col gap-5">
      <PeriodPanel period={d.current} ownOnly={d.ownOnly} manage={manage} />
      {!d.ownOnly && (
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-1">
          <h2 className="font-display text-xl">Unpaid from past periods</h2>
          <span className="text-sm text-ink-soft">{d.periods.length ? <>Owed: <span className="font-semibold tabular-nums text-ink">{formatCents(d.owedCents)}</span></> : "Everyone is paid up."}</span>
        </div>
      )}
      {d.ownOnly && d.periods.length > 0 && <h2 className="px-1 font-display text-xl">Not paid yet</h2>}
      {d.periods.map((p) => (
        <PeriodPanel key={p.start} period={p} ownOnly={d.ownOnly} manage={manage} />
      ))}
      {d.ownOnly && <OwnPaid />}
      {!d.ownOnly && <p className="px-1 text-sm text-muted">Paid payouts are in {can("reports.financial") ? <Link to="/reports/payouts" className="font-semibold text-brand">Reports → Payouts</Link> : "Reports → Payouts"}.</p>}
    </div>
  );
}

function PeriodPanel({ period: p, ownOnly, manage }: { period: UnpaidPeriod; ownOnly: boolean; manage: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const range = periodRange(p.start, p.end);
  const refresh = () => qc.invalidateQueries({ queryKey: ["payouts"] });
  const pay = useMutation({
    mutationFn: (v: { x: Payee; declined?: boolean; note?: string }) =>
      post("/api/payouts/payments", { periodStart: p.start, kind: v.x.kind, payeeId: v.x.kind === "staff" ? payoutPayeeId(v.x.nurse) : v.x.share.id, declined: !!v.declined, note: v.note ?? "" }),
    onSuccess: async () => {
      await refresh();
      await qc.invalidateQueries({ queryKey: ["payout-payments"] });
    },
  });
  const payees = payeesOf(p);
  return (
    <Panel>
      <PanelHeader
        title={range}
        description={p.current ? "This period so far: a running total. It can be marked paid once it ends." : undefined}
        action={
          <span className="flex flex-wrap items-center gap-2">
            {p.current && <Badge tone="brand">In progress</Badge>}
            <span className="font-semibold tabular-nums">{formatCents(p.totalCents)}</span>
            {payees.length > 0 && <ExportButtons name={`Payouts ${p.start} to ${p.end}${p.current ? " (so far)" : " (unpaid)"}`} tables={() => payoutTables(payees.map((payee) => ({ period: `${p.start} to ${p.end}`, payee })))} />}
          </span>
        }
      />
      {pay.error && (
        <div className="px-5 pt-3">
          <Alert>{errorMessage(pay.error)}</Alert>
        </div>
      )}
      <table className="table-base">
        <tbody>
          {payees.length === 0 && (
            <tr>
              <td className="text-muted">Nothing yet this period.</td>
            </tr>
          )}
          {payees.map((x) => {
            const key = x.kind === "staff" ? `n:${payoutPayeeId(x.nurse)}` : `s:${x.share.id}`;
            const busy = pay.isPending && pay.variables?.x === x && !pay.variables.declined;
            const declining = pay.isPending && pay.variables?.x === x && !!pay.variables.declined;
            return (
              <PayeeRows
                key={key}
                payee={x}
                open={open === key || (ownOnly && p.current)}
                onToggle={() => setOpen(open === key ? null : key)}
                canAdjust={manage && (x.kind === "share" || !!x.nurse.staffMemberId)}
                action={
                  manage && !p.current ? (
                    <span className="flex flex-wrap justify-end gap-2">
                      <Button
                        variant="secondary"
                        busy={declining}
                        disabled={pay.isPending && !declining}
                        title="They're not taking this payout (e.g. reinvesting in the company). It's settled for the period but not paid out."
                        onClick={() => {
                          const note = window.prompt(`Decline ${x.kind === "staff" ? x.nurse.name : x.share.name}'s payout for ${range}? It's settled for the period but not paid out (undo it in Reports → Payouts).\n\nNote (optional, e.g. "Reinvesting in the company"):`, "");
                          if (note !== null) pay.mutate({ x, declined: true, note: note.trim().slice(0, 300) });
                        }}
                      >
                        Decline
                      </Button>
                      <Button variant="primary" busy={busy} disabled={pay.isPending && !busy} onClick={() => pay.mutate({ x })}>
                        Mark paid
                      </Button>
                    </span>
                  ) : null
                }
              />
            );
          })}
        </tbody>
      </table>
      {manage && p.eventDays.map((d) => <EventDayEditor key={d.occurrenceId} day={d} />)}
      {manage && <AddAdjustment period={p} />}
    </Panel>
  );
}

/** For a datetime-local box (this computer's time, which is the clinic's). */
const localInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * An event day's pay: who worked when (each visit goes to whoever was working when it started,
 * shared evenly), and any visit's split changed by hand.
 */
function EventDayEditor({ day }: { day: PayoutEventDay }) {
  const qc = useQueryClient();
  const staff = useQuery({ queryKey: ["staff"], queryFn: () => get<StaffListItem[]>("/api/staff") });
  const people = (staff.data ?? []).filter((m) => m.status !== "deactivated");
  const nameOf = (id: string) => {
    const m = staff.data?.find((x) => x.id === id);
    return m ? `${m.firstName} ${m.lastName}` : "Former staff";
  };
  const [workers, setWorkers] = useState(day.workers.map((w) => ({ staffMemberId: w.staffMemberId, startsAt: localInput(w.startsAt), endsAt: localInput(w.endsAt) })));
  const [dirty, setDirty] = useState(false);
  const [picking, setPicking] = useState<{ appointmentId: string; ids: string[] } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["payouts"] });
  const saveHours = useMutation({
    mutationFn: (reset: boolean) =>
      put(`/api/payouts/events/${day.occurrenceId}/hours`, { workers: reset ? [] : workers.map((w) => ({ staffMemberId: w.staffMemberId, startsAt: new Date(w.startsAt).toISOString(), endsAt: new Date(w.endsAt).toISOString() })) }),
    onSuccess: async () => (setDirty(false), await refresh()),
  });
  useEffect(() => {
    if (!dirty) setWorkers(day.workers.map((w) => ({ staffMemberId: w.staffMemberId, startsAt: localInput(w.startsAt), endsAt: localInput(w.endsAt) })));
  }, [day]); // eslint-disable-line react-hooks/exhaustive-deps
  const split = useMutation({
    mutationFn: (v: { appointmentId: string; ids: string[] }) => put(`/api/payouts/visits/${v.appointmentId}/split`, { staffIds: v.ids }),
    onSuccess: async () => (setPicking(null), await refresh()),
  });
  const edit = (i: number, v: Partial<(typeof workers)[number]>) => (setDirty(true), setWorkers((cur) => cur.map((w, j) => (j === i ? { ...w, ...v } : w))));
  const nobody = day.visits.filter((v) => !v.staffIds.length).length;
  // Collapsed to one line unless a visit has nobody working (that needs a look).
  const [expanded, setExpanded] = useState(nobody > 0);
  const who = [...new Set(day.workers.map((w) => nameOf(w.staffMemberId)))].join(", ");
  if (!expanded)
    return (
      <div className="border-t border-line px-5 py-3">
        <button type="button" className="flex w-full flex-wrap items-baseline gap-x-2 text-left hover:text-brand" onClick={() => setExpanded(true)} aria-expanded={false}>
          <span aria-hidden>▸</span>
          <span className="font-semibold">{day.name}: who worked</span>
          <span className="text-sm text-muted">
            {who || "nobody yet"} · {day.visits.length} visit{day.visits.length === 1 ? "" : "s"}
            {day.edited ? " · hours edited" : ""}
          </span>
        </button>
      </div>
    );
  return (
    <div className="border-t border-line px-5 py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <button type="button" className="flex items-baseline gap-2 text-left hover:text-brand" onClick={() => setExpanded(false)} aria-expanded>
          <span aria-hidden>▾</span>
          <h3 className="font-semibold">{day.name}: who worked</h3>
        </button>
        {nobody > 0 && <Badge tone="wait">{nobody} visit{nobody === 1 ? "" : "s"} with nobody working</Badge>}
      </div>
      <p className="mt-1 text-sm text-muted">
        Each visit goes to whoever was working when it started, shared evenly (commission and tips). {day.edited ? "Hours edited." : "Starts as the nurses on the event, for its whole time."} Pay only: the schedule doesn't change.
      </p>
      <div className="mt-3 flex flex-col gap-2">
        {workers.map((w, i) => (
          <div key={i} className="flex flex-wrap items-end gap-2">
            <SelectField label="Nurse" value={w.staffMemberId} onChange={(e) => edit(i, { staffMemberId: e.target.value })}>
              {people.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.firstName} {m.lastName}
                </option>
              ))}
            </SelectField>
            <Field label="From" type="datetime-local" value={w.startsAt} onChange={(e) => edit(i, { startsAt: e.target.value })} />
            <Field label="To" type="datetime-local" value={w.endsAt} onChange={(e) => edit(i, { endsAt: e.target.value })} />
            <Button variant="quiet" className="text-danger" onClick={() => (setDirty(true), setWorkers((cur) => cur.filter((_, j) => j !== i)))}>
              Remove
            </Button>
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="quiet"
            disabled={!people.length}
            onClick={() => (setDirty(true), setWorkers((cur) => [...cur, { staffMemberId: people[0]!.id, startsAt: localInput(day.startsAt), endsAt: localInput(day.endsAt) }]))}
          >
            Add a nurse
          </Button>
          <Button variant="primary" busy={saveHours.isPending && saveHours.variables === false} disabled={!dirty || !workers.length} onClick={() => saveHours.mutate(false)}>
            Save hours
          </Button>
          {day.edited && (
            <Button variant="quiet" busy={saveHours.isPending && saveHours.variables === true} onClick={() => saveHours.mutate(true)}>
              Back to the event's nurses
            </Button>
          )}
        </div>
        {saveHours.error && <Alert>{errorMessage(saveHours.error)}</Alert>}
      </div>
      <table className="table-base mt-3 text-sm">
        <tbody>
          {day.visits.map((v) => (
            <tr key={v.appointmentId}>
              <td className="w-24">{v.time}</td>
              <td>
                {v.client}
                <div className="text-xs text-muted">{v.service}</div>
              </td>
              <td>
                {picking?.appointmentId === v.appointmentId ? (
                  <div className="flex flex-wrap items-center gap-3">
                    {people.map((m) => (
                      <Checkbox
                        key={m.id}
                        label={`${m.firstName} ${m.lastName}`}
                        checked={picking.ids.includes(m.id)}
                        onChange={(on) => setPicking({ ...picking, ids: on ? [...picking.ids, m.id] : picking.ids.filter((x) => x !== m.id) })}
                      />
                    ))}
                    <Button variant="primary" busy={split.isPending} disabled={!picking.ids.length} onClick={() => split.mutate(picking)}>
                      Save
                    </Button>
                    <Button variant="quiet" onClick={() => setPicking(null)}>
                      Cancel
                    </Button>
                  </div>
                ) : v.staffIds.length ? (
                  <span>
                    {v.staffIds.map(nameOf).join(", ")}
                    {v.byHand ? <span className="text-xs text-muted"> (by hand)</span> : null}
                  </span>
                ) : (
                  <Badge tone="wait">Nobody working</Badge>
                )}
              </td>
              <td className="w-px whitespace-nowrap text-right">
                {picking?.appointmentId !== v.appointmentId && (
                  <>
                    <Button variant="quiet" onClick={() => setPicking({ appointmentId: v.appointmentId, ids: v.staffIds })}>
                      Change
                    </Button>
                    {v.byHand && (
                      <Button variant="quiet" busy={split.isPending && split.variables?.appointmentId === v.appointmentId && !split.variables.ids.length} onClick={() => split.mutate({ appointmentId: v.appointmentId, ids: [] })}>
                        Use hours
                      </Button>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {split.error && <Alert>{errorMessage(split.error)}</Alert>}
    </div>
  );
}

/** A nurse's or revenue share's row (click for the lines), with an optional button. */
export function PayeeRows({ payee: x, open, onToggle, canAdjust, action }: { payee: Payee; open: boolean; onToggle: () => void; canAdjust?: boolean; action?: ReactNode }) {
  const qc = useQueryClient();
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/payouts/adjustments/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["payouts"] }) });
  const name = x.kind === "staff" ? x.nurse.name : x.share.name;
  const amount = x.kind === "staff" ? x.nurse.totalCents : x.share.amountCents;
  const sub =
    x.kind === "staff"
      ? [
          `${x.nurse.visits} visit${x.nurse.visits === 1 ? "" : "s"}`,
          `${formatCents(x.nurse.commissionCents)} commission`,
          x.nurse.overheadCents ? `after ${formatCents(x.nurse.overheadCents)} overhead` : null,
          x.nurse.tipsCents ? `${formatCents(x.nurse.tipsCents)} tips` : null,
          x.nurse.adjustmentsCents ? `${formatCents(x.nurse.adjustmentsCents)} bonuses and corrections` : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : [
          x.share.month ? `${x.share.month.soFar ? "This month so far" : "Month"}: ${dayLabel(x.share.month.start, { month: "short", day: "numeric" })}–${dayLabel(x.share.month.end, { month: "short", day: "numeric" })}` : null,
          `${x.share.percent}% of ${formatCents(x.share.baseCents)}`,
          x.share.adjustmentsCents ? `${formatCents(x.share.adjustmentsCents)} bonuses and corrections` : null].filter(Boolean).join(" · ");
  return (
    <>
      <tr className="cursor-pointer hover:bg-mist/60" onClick={onToggle}>
        <td>
          <div className="font-semibold">
            {open ? "▾" : "▸"} {name} {x.kind === "share" && <span className="font-normal text-muted">(revenue share)</span>}
          </div>
          <div className="pl-4 text-sm text-muted">{sub}</div>
        </td>
        <td className="text-right font-semibold tabular-nums">{formatCents(amount)}</td>
        {action !== undefined && (
          <td className="w-px whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
            {action}
          </td>
        )}
      </tr>
      {open && (
        <tr>
          <td colSpan={action !== undefined ? 3 : 2} className="bg-mist/40 p-0">
            <div className="overflow-x-auto px-3 py-3">
              {x.kind === "staff" ? (
                <table className="table-base text-sm">
                  <thead>
                    <tr>
                      <th>Paid</th>
                      <th>Client</th>
                      <th>Item</th>
                      <th className="text-right">Price</th>
                      <th className="text-right">Base</th>
                      <th className="text-right">Overhead</th>
                      <th className="text-right">%</th>
                      <th className="text-right">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {x.nurse.lines.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-muted">
                          No services{x.nurse.tipsCents ? " (tips only)" : ""}.
                        </td>
                      </tr>
                    )}
                    {x.nurse.lines.map((l, i) => (
                      <tr key={i}>
                        <td>{l.time ?? dayLabel(l.date, { month: "short", day: "numeric" })}</td>
                        <td>
                          {l.client}
                          {l.event && <div className="text-xs text-muted">{l.event}{l.sharedBy ? ` · shared by ${l.sharedBy}` : ""}</div>}
                          {!l.event && l.nurse !== undefined && <div className="text-xs text-muted">{l.nurse ? `Nurse: ${l.nurse}` : "No nurse"} · nobody was working: give it to someone below</div>}
                        </td>
                        <td>
                          {l.name}
                          {l.quantity > 1 ? ` × ${l.quantity}` : ""}
                          {l.note && <div className="text-xs text-muted">{l.note}</div>}
                          {!!l.tipCents && <div className="text-xs text-muted">Tip {formatCents(l.tipCents)}</div>}
                        </td>
                        <td className="text-right tabular-nums">{formatCents(l.priceCents)}</td>
                        <td className="text-right tabular-nums">{formatCents(l.baseCents)}</td>
                        <td className="text-right tabular-nums text-muted">{l.overheadCents ? `−${formatCents(l.overheadCents)}` : ""}</td>
                        <td className="text-right tabular-nums">{l.percent}</td>
                        <td className="text-right tabular-nums">{formatCents(l.amountCents)}</td>
                      </tr>
                    ))}
                    {x.nurse.tipsCents > 0 && (
                      <tr>
                        <td colSpan={7}>Tips</td>
                        <td className="text-right tabular-nums">{formatCents(x.nurse.tipsCents)}</td>
                      </tr>
                    )}
                    {x.nurse.adjustments.map((a) => (
                      <tr key={a.id}>
                        <td colSpan={7}>
                          <span className="font-semibold">{a.amountCents > 0 ? "Bonus" : "Correction"}:</span> {a.note} <span className="text-xs text-muted">({a.by})</span>
                          {canAdjust && (
                            <button type="button" className="ml-2 text-xs font-semibold text-danger" onClick={() => remove.mutate(a.id)}>
                              Remove
                            </button>
                          )}
                        </td>
                        <td className="text-right tabular-nums">{formatCents(a.amountCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="table-base text-sm">
                  <tbody>
                    {x.share.lines.map((l, i) => (
                      <tr key={i}>
                        <td>{dayLabel(l.date, { month: "short", day: "numeric" })}</td>
                        <td>{l.client}</td>
                        <td>{l.name}</td>
                        <td className="capitalize text-muted">{l.context}</td>
                        <td className="text-right tabular-nums">{formatCents(l.baseCents)}</td>
                        <td className="text-right tabular-nums">{formatCents(l.amountCents)}</td>
                      </tr>
                    ))}
                    {(x.share.adjustments ?? []).map((a) => (
                      <tr key={a.id}>
                        <td colSpan={5}>
                          <span className="font-semibold">{a.amountCents > 0 ? "Bonus" : "Correction"}:</span> {a.note} <span className="text-xs text-muted">({a.by})</span>
                          {canAdjust && (
                            <button type="button" className="ml-2 text-xs font-semibold text-danger" onClick={() => remove.mutate(a.id)}>
                              Remove
                            </button>
                          )}
                        </td>
                        <td className="text-right tabular-nums">{formatCents(a.amountCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {remove.error && <Alert>{errorMessage(remove.error)}</Alert>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** A payment as a payee row. */
export const payeeOfPayment = (p: PayoutPayment): Payee => (p.kind === "staff" ? { kind: "staff", nurse: p.detail as PayoutNurse } : { kind: "share", share: p.detail as PayoutShare });

/** A nurse's own payouts already paid. */
function OwnPaid() {
  const paid = useQuery({ queryKey: ["payout-payments", "own"], queryFn: () => get<PayoutPayment[]>("/api/payouts/payments") });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Panel>
      <PanelHeader title="Paid" />
      {paid.isLoading ? (
        <Loading />
      ) : paid.error ? (
        <Alert>{errorMessage(paid.error)}</Alert>
      ) : (
        <table className="table-base">
          <tbody>
            {paid.data!.length === 0 && (
              <tr>
                <td className="text-muted">Nothing marked paid yet.</td>
              </tr>
            )}
            {paid.data!.map((p) => (
              <PayeeRows
                key={p.id}
                payee={payeeOfPayment(p)}
                open={open === p.id}
                onToggle={() => setOpen(open === p.id ? null : p.id)}
                action={<span className="text-sm text-muted">{periodRange(p.periodStart, p.periodEnd)} · {p.declined ? "declined" : "paid"} {dayLabel(p.paidAt.slice(0, 10), { month: "short", day: "numeric" })}</span>}
              />
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function AddAdjustment({ period }: { period: UnpaidPeriod }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const staff = useQuery({ queryKey: ["staff"], queryFn: () => get<StaffListItem[]>("/api/staff"), enabled: can("staff.manage") });
  const people = staff.data?.filter((s) => s.status !== "deactivated").map((s) => ({ id: s.id, name: `${s.firstName} ${s.lastName}` })) ?? period.nurses.filter((n) => n.staffMemberId).map((n) => ({ id: n.staffMemberId!, name: n.name }));
  // Revenue shares (ISP, medical director…) can have a bonus or correction too.
  const settings = useQuery({ queryKey: ["payout-settings"], queryFn: () => get<Settings>("/api/payouts/settings") });
  const shares = (settings.data?.shares ?? []).filter((sh) => sh.active);
  const [show, setShow] = useState(false);
  // "s:<id>" = a staff member, "r:<id>" = a revenue share.
  const [v, setV] = useState({ payee: "", amount: "", note: "", sign: "1" });
  const add = useMutation({
    mutationFn: () =>
      post("/api/payouts/adjustments", {
        ...(v.payee.startsWith("r:") ? { revenueShareId: v.payee.slice(2) } : { staffMemberId: v.payee.slice(2) }),
        periodStart: period.start,
        amountCents: Number(v.sign) * (parseDollars(v.amount) ?? 0),
        note: v.note,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["payouts"] });
      setV({ payee: "", amount: "", note: "", sign: "1" });
      setShow(false);
    },
  });
  if (!show)
    return (
      <div className="border-t border-line px-5 py-3">
        <button type="button" className="text-sm font-semibold text-brand" onClick={() => setShow(true)}>
          + Bonus or correction
        </button>
      </div>
    );
  return (
    <form
      className="flex flex-wrap items-end gap-3 border-t border-line px-5 py-4"
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate();
      }}
    >
      <SelectField label="Bonus or correction for" value={v.payee} onChange={(e) => setV({ ...v, payee: e.target.value })}>
        <option value="">Pick who…</option>
        {people.map((p) => (
          <option key={p.id} value={`s:${p.id}`}>
            {p.name}
          </option>
        ))}
        {shares.length > 0 && (
          <optgroup label="Revenue shares">
            {shares.map((sh) => (
              <option key={sh.id} value={`r:${sh.id}`}>
                {sh.name} (revenue share)
              </option>
            ))}
          </optgroup>
        )}
      </SelectField>
      <SelectField label="Type" value={v.sign} onChange={(e) => setV({ ...v, sign: e.target.value })}>
        <option value="1">Add (bonus)</option>
        <option value="-1">Take off (correction)</option>
      </SelectField>
      <Field label="Amount ($)" inputMode="decimal" className="w-28" value={v.amount} onChange={(e) => setV({ ...v, amount: e.target.value })} />
      <Field label="What it's for" className="min-w-48 flex-1" maxLength={200} value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} />
      <Button type="submit" busy={add.isPending} disabled={!v.payee || !v.amount || !v.note.trim()}>
        Add
      </Button>
      <Button variant="quiet" onClick={() => setShow(false)}>
        Cancel
      </Button>
      {add.error && <Alert>{errorMessage(add.error)}</Alert>}
    </form>
  );
}

function RatesTab() {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["payout-settings"], queryFn: () => get<Settings>("/api/payouts/settings") });
  const staff = useQuery({ queryKey: ["staff"], queryFn: () => get<StaffListItem[]>("/api/staff") });
  const catalog = useCatalog();
  const items = (catalog.data?.items ?? []).filter((i: CatalogItem) => (i.type === "service" || i.type === "add_on") && i.status === "active");
  const [base, setBase] = useState<{ defaultPercent: string; schedule: PayoutSchedule; anchorDate: string } | null>(null);
  useEffect(() => {
    if (settings.data) setBase({ defaultPercent: String(settings.data.defaultPercent), schedule: settings.data.schedule, anchorDate: settings.data.anchorDate });
  }, [settings.data]);
  const [notice, setNotice] = useState<string | null>(null);
  const saveBase = useMutation({
    mutationFn: () => put("/api/payouts/settings", { defaultPercent: Number(base!.defaultPercent), schedule: base!.schedule, anchorDate: base!.anchorDate }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["payout-settings"] });
      await qc.invalidateQueries({ queryKey: ["payouts"] });
      setNotice("Saved.");
    },
  });
  const [rate, setRate] = useState({ staffMemberId: "", catalogItemId: "", percent: "" });
  const addRate = useMutation({
    mutationFn: () => post("/api/payouts/rates", { staffMemberId: rate.staffMemberId || null, catalogItemId: rate.catalogItemId || null, percent: Number(rate.percent) }),
    onSuccess: async () => {
      setRate({ staffMemberId: "", catalogItemId: "", percent: "" });
      await qc.invalidateQueries({ queryKey: ["payout-settings"] });
      await qc.invalidateQueries({ queryKey: ["payouts"] });
    },
  });
  const removeRate = useMutation({
    mutationFn: (id: string) => del(`/api/payouts/rates/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["payout-settings"] });
      await qc.invalidateQueries({ queryKey: ["payouts"] });
    },
  });
  if (settings.isLoading || !base) return settings.error ? <Alert>{errorMessage(settings.error)}</Alert> : <Loading />;
  const serviceRates = settings.data!.rates.filter((r) => !r.staffMemberId);
  const nurseRates = settings.data!.rates.filter((r) => r.staffMemberId);
  const staffName = (id: string | null) => {
    const s = staff.data?.find((x) => x.id === id);
    return id ? (s ? `${s.firstName} ${s.lastName}` : "Former staff") : "Any nurse";
  };
  const itemName = (id: string | null) => (id ? (catalog.data?.items.find((i: CatalogItem) => i.id === id)?.name ?? "Removed item") : "Any service");

  return (
    <div className="flex flex-col gap-6">
      <Panel>
        <PanelHeader title="Default and pay periods" />
        <form
          className="flex flex-col gap-4 px-5 py-5"
          onSubmit={(e) => {
            e.preventDefault();
            setNotice(null);
            saveBase.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Commission (%)" type="number" min={0} max={100} step="any" value={base.defaultPercent} onChange={(e) => setBase({ ...base, defaultPercent: e.target.value })} hint="Unless a rate below says otherwise. Tips are always 100%." />
            <SelectField label="Pay periods" value={base.schedule} onChange={(e) => setBase({ ...base, schedule: e.target.value as PayoutSchedule })}>
              {PAYOUT_SCHEDULES.map((s) => (
                <option key={s} value={s}>
                  {PAYOUT_SCHEDULE_LABELS[s]}
                </option>
              ))}
            </SelectField>
            {(base.schedule === "weekly" || base.schedule === "biweekly") && (
              <Field label="A period starts on" type="date" value={base.anchorDate} onChange={(e) => setBase({ ...base, anchorDate: e.target.value })} hint="Any past first day of a pay period." />
            )}
          </div>
          {saveBase.error && <Alert>{errorMessage(saveBase.error)}</Alert>}
          {notice && <Alert tone="ok">{notice}</Alert>}
          <Button type="submit" variant="primary" className="self-start" busy={saveBase.isPending}>
            Save
          </Button>
        </form>
      </Panel>

      <Panel>
        <PanelHeader title="Service rates" description="A different commission for one service or add-on, for every nurse. A nurse's own rates are on their staff page (Staff → name → Pay), and beat these." />
        <table className="table-base">
          <tbody>
            {serviceRates.length === 0 && (
              <tr>
                <td className="text-muted">None: every service pays {settings.data!.defaultPercent}% unless a nurse has their own rate.</td>
              </tr>
            )}
            {serviceRates.map((r) => (
              <tr key={r.id}>
                <td>{itemName(r.catalogItemId)}</td>
                <td className="text-right tabular-nums">{r.percent}%</td>
                <td className="text-right">
                  <Button variant="quiet" onClick={() => removeRate.mutate(r.id)}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <form
          className="flex flex-wrap items-end gap-3 border-t border-line px-5 py-4"
          onSubmit={(e) => {
            e.preventDefault();
            addRate.mutate();
          }}
        >
          <SelectField label="Service or add-on" value={rate.catalogItemId} onChange={(e) => setRate({ ...rate, catalogItemId: e.target.value })}>
            <option value="">Pick one</option>
            {items.map((i: CatalogItem) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </SelectField>
          <Field label="Commission (%)" type="number" min={0} max={100} step="any" className="w-32" value={rate.percent} onChange={(e) => setRate({ ...rate, percent: e.target.value })} />
          <Button type="submit" busy={addRate.isPending} disabled={rate.percent === "" || !rate.catalogItemId}>
            Set rate
          </Button>
          {addRate.error && <Alert>{errorMessage(addRate.error)}</Alert>}
        </form>
        {nurseRates.length > 0 && (
          <p className="border-t border-line px-5 py-3 text-sm text-ink-soft">
            Nurses with their own rates:{" "}
            {[...new Set(nurseRates.map((r) => r.staffMemberId!))].map((id, i) => (
              <span key={id}>
                {i > 0 && ", "}
                <Link to={`/staff/${id}`} className="font-semibold text-brand">
                  {staffName(id)}
                </Link>
              </span>
            ))}
          </p>
        )}
      </Panel>

      <RevenueShares shares={settings.data!.shares} />
    </div>
  );
}

const CONTEXT_LABELS = { clinic: "At the clinic", mobile: "Mobile", event: "Events" } as const;
const blankShare: RevenueShareInput = { name: "", percent: 10, contexts: ["clinic"], items: "all", includeProducts: false, includeTravel: false, monthly: false, active: true };

/** Revenue shares: a percent of what clients paid, for the medical director, the landlord, and the like. */
function RevenueShares({ shares }: { shares: RevenueShare[] }) {
  const [editing, setEditing] = useState<RevenueShare | "new" | null>(null);
  const describe = (sh: RevenueShare) =>
    [
      sh.items === "gfe" ? "services that need a good faith exam (and add-ons on them)" : "all services and add-ons",
      sh.includeProducts ? "products" : null,
      sh.includeTravel ? "travel fees" : null,
    ]
      .filter(Boolean)
      .join(", ") + ` · ${sh.contexts.map((c) => CONTEXT_LABELS[c].toLowerCase()).join(", ")}` + (sh.monthly ? " · paid monthly" : "");
  return (
    <Panel>
      <PanelHeader
        title="Revenue shares"
        description="A percent of what clients paid per visit, for the medical director, the landlord, and so on. Never on tips or no-show fees. Listed with the nurses under each pay period, and marked paid the same way."
        action={
          <Button variant="secondary" onClick={() => setEditing("new")}>
            Add a share
          </Button>
        }
      />
      <ul className="divide-y divide-mist">
        {shares.length === 0 && <li className="px-5 py-4 text-sm text-muted">None yet.</li>}
        {shares.map((sh) => (
          <li key={sh.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <div>
              <div className="font-semibold">
                {sh.name} · {sh.percent}% {!sh.active && <Badge tone="off">Off</Badge>}
              </div>
              <div className="text-sm text-muted">Of {describe(sh)}</div>
            </div>
            <Button variant="quiet" onClick={() => setEditing(sh)}>
              Edit
            </Button>
          </li>
        ))}
      </ul>
      {editing && <ShareEditor share={editing === "new" ? null : editing} onDone={() => setEditing(null)} />}
    </Panel>
  );
}

function ShareEditor({ share, onDone }: { share: RevenueShare | null; onDone: () => void }) {
  const qc = useQueryClient();
  const [v, setV] = useState<RevenueShareInput>(share ? { name: share.name, percent: share.percent, contexts: share.contexts, items: share.items, includeProducts: share.includeProducts, includeTravel: share.includeTravel, monthly: share.monthly, active: share.active } : blankShare);
  const done = async () => {
    await qc.invalidateQueries({ queryKey: ["payout-settings"] });
    await qc.invalidateQueries({ queryKey: ["payouts"] });
    onDone();
  };
  const save = useMutation({ mutationFn: () => (share ? put(`/api/payouts/shares/${share.id}`, v) : post("/api/payouts/shares", v)), onSuccess: done });
  const remove = useMutation({ mutationFn: () => del(`/api/payouts/shares/${share!.id}`), onSuccess: done });
  const toggle = (c: RevenueShareInput["contexts"][number], on: boolean) => setV({ ...v, contexts: on ? [...v.contexts, c] : v.contexts.filter((x) => x !== c) });
  return (
    <form
      className="flex flex-col gap-4 border-t border-line px-5 py-5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid gap-4 sm:grid-cols-[1fr_10rem]">
        <Field label="Who it's for" required placeholder="Medical director, ISP (rent)" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
        <Field label="Percent" type="number" min={0} max={100} step="any" required value={String(v.percent)} onChange={(e) => setV({ ...v, percent: Number(e.target.value) })} />
      </div>
      <SelectField label="Of" value={v.items} onChange={(e) => setV({ ...v, items: e.target.value as RevenueShareInput["items"] })}>
        <option value="all">All services and add-ons</option>
        <option value="gfe">Only services that need a good faith exam (and add-ons on them)</option>
      </SelectField>
      <fieldset className="flex flex-wrap gap-x-6 gap-y-2">
        <legend className="mb-1 text-sm font-semibold text-ink-soft">Also</legend>
        <Checkbox label="Products" checked={v.includeProducts} onChange={(on) => setV({ ...v, includeProducts: on })} />
        <Checkbox label="Mobile travel fees" checked={v.includeTravel} onChange={(on) => setV({ ...v, includeTravel: on })} />
      </fieldset>
      <fieldset className="flex flex-wrap gap-x-6 gap-y-2">
        <legend className="mb-1 text-sm font-semibold text-ink-soft">Where visits count</legend>
        {(["clinic", "mobile", "event"] as const).map((c) => (
          <Checkbox key={c} label={CONTEXT_LABELS[c]} checked={v.contexts.includes(c)} onChange={(on) => toggle(c, on)} />
        ))}
      </fieldset>
      <SelectField
        label="Paid"
        value={v.monthly ? "monthly" : "period"}
        onChange={(e) => setV({ ...v, monthly: e.target.value === "monthly" })}
        hint={v.monthly ? "Listed once a month, on the pay period holding the month's last day, covering the whole month. Starts with this month; earlier periods stay as they were." : undefined}
      >
        <option value="period">Every pay period, like the nurses</option>
        <option value="monthly">Once a month</option>
      </SelectField>
      {share && <Checkbox label="On" description="Turn off to stop counting it (what's already marked paid keeps its figures; unpaid periods drop it)." checked={v.active} onChange={(on) => setV({ ...v, active: on })} />}
      {(save.error || remove.error) && <Alert>{errorMessage(save.error ?? remove.error)}</Alert>}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" busy={save.isPending}>
          Save
        </Button>
        <Button variant="quiet" onClick={onDone}>
          Cancel
        </Button>
        {share && (
          <Button variant="quiet" className="ml-auto text-danger" busy={remove.isPending} onClick={() => window.confirm(`Remove ${share.name}?`) && remove.mutate()}>
            Remove
          </Button>
        )}
      </div>
    </form>
  );
}
